import type { Access, AccessPlan, PlanFeatures, PlanOffer } from "./auth-api";

/**
 * Ограничения тарифа на экране (stage9-billing-gating, Д10). Что именно закрыто,
 * решает демон; клиент только называет замок и тариф, который снимает его.
 * Признаки берутся из `access.features` и `plans[]` ответа `me`, а не
 * выводятся из потока.
 */

/** Полный доступ: тот, что у `pro`, и тот, что считается при отсутствии `features`. */
export const FULL_FEATURES: PlanFeatures = {
  exchanges: "all",
  mm_approaches_library: true,
  sound_notify: true,
  open_cards: null,
};

const UNAVAILABLE_TEXT = "Недоступно на вашем тарифе";

/** Имя тарифа в тексте: «Trader», «Pro». */
export function planLabel(plan: AccessPlan): string {
  return plan === "free" ? "Free" : plan === "trader" ? "Trader" : "Pro";
}

/** Подпись на значке профиля: «FREE», «TRADER», «PRO». */
export function planTierLabel(plan: AccessPlan): string {
  return plan.toUpperCase();
}

/** Строка тарифа в шапке меню профиля: «Тариф: Trader». */
export function planMenuLabel(plan: AccessPlan): string {
  return `Тариф: ${planLabel(plan)}`;
}

/**
 * Тариф для подписи: только при включённом биллинге (при выключенном ничего
 * нового на экране не появляется) и только когда доступ уже известен.
 */
export function visibleTier(access: Access | null): AccessPlan | null {
  return access?.billing_enabled === true ? access.plan : null;
}

/** «Доступно на тарифе Trader»; тарифа, дающего нужное, нет — «Недоступно на вашем тарифе». */
export function availabilityText(plan: AccessPlan | null): string {
  return plan === null ? UNAVAILABLE_TEXT : `Доступно на тарифе ${planLabel(plan)}`;
}

/** Первый (самый дешёвый — `plans` идут по возрастанию цены) тариф, удовлетворяющий условию. */
function nearestPlan(
  plans: readonly PlanOffer[],
  gives: (features: PlanFeatures) => boolean,
): AccessPlan | null {
  return plans.find((offer) => gives(offer.features))?.plan ?? null;
}

export interface PlanGate {
  /** Биллинг включён: только тогда на экране появляются замки и подпись тарифа. */
  active: boolean;
  features: PlanFeatures;
  /** Тариф для подписи на значке; `null` — биллинг выключен или доступ не прочитан. */
  tier: AccessPlan | null;
  /** Пометки ММ и подходы закрыты. */
  mmApproachesLocked: boolean;
  /** Библиотека закрыта тем же признаком, что и подходы. */
  libraryLocked: boolean;
  /** Звук и уведомления закрыты — экран «Алерты» показывает замок. */
  alertsLocked: boolean;
  /** Список ситуаций закрыт: тариф с лимитом открытых карточек. */
  situationsLocked: boolean;
  mmApproachesText: string;
  soundText: string;
  situationsText: string;
  /** Текст закрытой карточки биржи. */
  textForExchange: (exchange: string) => string;
}

export function buildPlanGate(access: Access | null, plans: readonly PlanOffer[]): PlanGate {
  const active = access?.billing_enabled === true;
  if (active && access?.features === undefined) {
    console.error("billing_enabled без features в ответе me: откат на полный доступ");
  }
  const features = access?.features ?? FULL_FEATURES;
  const mmApproachesText = availabilityText(
    nearestPlan(plans, (offered) => offered.mm_approaches_library),
  );
  return {
    active,
    features,
    tier: visibleTier(access),
    mmApproachesLocked: active && !features.mm_approaches_library,
    libraryLocked: active && !features.mm_approaches_library,
    alertsLocked: active && !features.sound_notify,
    situationsLocked: active && features.open_cards !== null,
    mmApproachesText,
    soundText: availabilityText(nearestPlan(plans, (offered) => offered.sound_notify)),
    situationsText: availabilityText(nearestPlan(plans, (offered) => offered.open_cards === null)),
    textForExchange: (exchange) =>
      availabilityText(
        nearestPlan(
          plans,
          // Карточка закрыта лимитом открытых: биржа в составе не снимает замок,
          // его снимает только тариф без лимита.
          (offered) =>
            offered.open_cards === null &&
            (offered.exchanges === "all" || offered.exchanges.includes(exchange)),
        ),
      ),
  };
}

/** Шлюз без ограничений: так живут экраны вне провайдера и при выключенном биллинге. */
export const INACTIVE_PLAN_GATE: PlanGate = buildPlanGate(null, []);
